// discoveryCut — pure client-side dendrogram cutting for WP-D3b category
// discovery. The server stores the full average-linkage dendrogram
// ({sessions, merges}); the Explorer re-cuts it at any threshold instantly,
// so the author picks cluster granularity by eye with a slider instead of
// pre-agreeing an absolute cosine value (plan v2 §4a D3b.3).
//
// Node ids: leaves are 0..n-1 (indices into sessions), merge k is node n+k.
// A merge applies when height <= threshold; merges are applied in
// construction order (children precede parents) via union-find over
// representative leaves, which stays well-defined even on the rare
// average-linkage inversion.

/**
 * Cut the dendrogram at a cosine-distance threshold.
 * @param {{sessions: string[], merges: Array<{a:number,b:number,height:number,size:number,medoid:number}>}} payload
 * @param {number} threshold
 * @returns {{clusters: Array<{members:number[], size:number, medoid:number}>, unclustered: number[], n: number}}
 *   clusters sorted by size desc (medoid asc tiebreak); members/medoid are
 *   leaf indices into payload.sessions; singletons pooled in `unclustered`.
 *   Σ cluster sizes + unclustered.length === n always (nothing dropped).
 */
export function cutDendrogram(payload, threshold) {
    const sessions = Array.isArray(payload?.sessions) ? payload.sessions : [];
    const merges = Array.isArray(payload?.merges) ? payload.merges : [];
    const n = sessions.length;

    const parent = Array.from({ length: n }, (_, i) => i);
    const find = (x) => {
        while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; }
        return x;
    };

    // node id → a representative leaf beneath it (leaves map to themselves).
    const repLeaf = new Array(n + merges.length);
    for (let i = 0; i < n; i++) repLeaf[i] = i;

    // Applied-merge medoid per component root; the LAST applied merge in a
    // component sets its medoid.
    const compMedoid = new Map();

    merges.forEach((m, k) => {
        const node = n + k;
        const la = repLeaf[m.a];
        const lb = repLeaf[m.b];
        repLeaf[node] = la != null ? la : lb;
        if (la == null || lb == null) return;
        if (Number(m.height) <= threshold) {
            const ra = find(la);
            const rb = find(lb);
            if (ra !== rb) parent[rb] = ra;
            compMedoid.set(find(ra), m.medoid);
        }
    });

    const byRoot = new Map();
    for (let i = 0; i < n; i++) {
        const r = find(i);
        if (!byRoot.has(r)) byRoot.set(r, []);
        byRoot.get(r).push(i);
    }

    const clusters = [];
    const unclustered = [];
    byRoot.forEach((members, root) => {
        if (members.length === 1) {
            unclustered.push(members[0]);
            return;
        }
        let medoid = compMedoid.get(root);
        if (medoid == null || !members.includes(medoid)) medoid = members[0];
        clusters.push({ members, size: members.length, medoid });
    });

    clusters.sort((a, b) => (b.size - a.size) || (a.medoid - b.medoid));
    unclustered.sort((a, b) => a - b);
    return { clusters, unclustered, n };
}

/**
 * Suggested default threshold: the midpoint of the largest gap between
 * consecutive sorted merge heights — the natural "within-cluster vs.
 * between-cluster" separation. With a single merge, includes it.
 */
export function defaultThreshold(merges) {
    const hs = (Array.isArray(merges) ? merges : [])
        .map(m => Number(m.height))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);
    if (hs.length === 0) return 0;
    if (hs.length === 1) return hs[0];
    let bestGap = -1;
    let cut = hs[hs.length - 1];
    for (let i = 0; i < hs.length - 1; i++) {
        const gap = hs[i + 1] - hs[i];
        if (gap > bestGap) { bestGap = gap; cut = (hs[i] + hs[i + 1]) / 2; }
    }
    return cut;
}

/** Max merge height (slider upper bound); 0 when there are no merges. */
export function maxHeight(merges) {
    let max = 0;
    (Array.isArray(merges) ? merges : []).forEach(m => {
        const h = Number(m.height);
        if (Number.isFinite(h) && h > max) max = h;
    });
    return max;
}
